import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useSelector } from "react-redux";
import {
  User,
  Lock,
  MapPin,
  Briefcase,
  Hash,
  Building2,
  Calendar,
  Eye,
  EyeOff,
  Save,
  Key,
  X,
} from "lucide-react";
import { format } from "date-fns";

// Ensure these imports point to your correct directories
import { domain } from "../../../security";
import Loader from "../../../components/loader/Loader";
import profilePlaceholder from "../../../Images/profile.jpg";
import { selectUserID, selectImgUrl } from "../../../redux/IchthusSlice";

const ManageAccount = () => {
  const currentUserID = useSelector(selectUserID);
  // Grab the image directly from Redux
  const reduxProfileImage = useSelector(selectImgUrl);

  const [userData, setUserData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Password editing states
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Toggle state to hide/show password fields
  const [isEditingPassword, setIsEditingPassword] = useState(false);

  // 1. Safe fetch of exact User/Employee link from server
  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const userResponse = await axios.get(
          `${domain}/api/Users/${currentUserID}`,
        );
        const fetchedUser = userResponse.data;
        setUserData(fetchedUser);

        if (fetchedUser.employeeId) {
          const empResponse = await axios.get(
            `${domain}/api/Employees/${fetchedUser.employeeId}`,
          );
          setEmployeeData(empResponse.data);
        }
      } catch (error) {
        toast.error("Failed to load account details.");
      } finally {
        setLoading(false);
      }
    };

    if (currentUserID) fetchAccountDetails();
  }, [currentUserID]);

  // 2. Change Password Submission
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!password) {
      toast.warning("Please enter a new password.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    setIsSaving(true);
    try {
      const updatePayload = {
        EmployeeId: userData.employeeId,
        RoleId: userData.roleId,
        UserID: currentUserID,
        Password: password,
      };

      await axios.put(`${domain}/api/Users/${currentUserID}`, updatePayload);
      toast.success("Password updated successfully!");

      // Close the form and clear fields on success
      handleCancelEdit();
    } catch (error) {
      toast.error("Failed to update password. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingPassword(false);
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return format(new Date(dateString), "MMM dd, yyyy");
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  // Use Redux image first, fallback to employee database image, then default placeholder
  const displayImage =
    reduxProfileImage || employeeData?.employeeImage || profilePlaceholder;

  return (
    <div className="min-h-screen pb-12">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Area */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <User className="text-indigo-600" /> Manage Your Account
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            View your employee profile and manage your security credentials.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* LEFT COLUMN: Employee Details */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col items-center text-center">
            <div className="relative mb-4">
              <img
                src={displayImage}
                alt="Profile"
                className="w-28 h-28 object-cover rounded-full border-4 border-indigo-50 shadow-md"
              />
              <span className="absolute bottom-1 right-1 bg-emerald-500 w-5 h-5 border-2 border-white rounded-full"></span>
            </div>

            <h2 className="text-xl font-bold text-slate-800">
              {employeeData
                ? `${employeeData.firstName} ${employeeData.lastName}`
                : userData?.userName}
            </h2>
            <p className="text-sm font-medium text-indigo-600 mt-1 bg-indigo-50 px-3 py-1 rounded-full">
              {employeeData?.position || "System User"}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">
              Profile Information
            </h3>

            {employeeData ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-50 text-slate-400 rounded-lg">
                    <Hash size={16} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">
                      Employee ID
                    </p>
                    <p className="text-sm text-slate-700 font-semibold">
                      {employeeData.id}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-50 text-slate-400 rounded-lg">
                    <Building2 size={16} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">
                      Department
                    </p>
                    <p className="text-sm text-slate-700 font-semibold">
                      {employeeData.department || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-50 text-slate-400 rounded-lg">
                    <Briefcase size={16} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">
                      Employment Type
                    </p>
                    <p className="text-sm text-slate-700 font-semibold">
                      {employeeData.employmentType || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-50 text-slate-400 rounded-lg">
                    <MapPin size={16} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">
                      Location
                    </p>
                    <p className="text-sm text-slate-700 font-semibold">
                      {employeeData.location?.locationName || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-50 text-slate-400 rounded-lg">
                    <Calendar size={16} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">
                      Date Hired
                    </p>
                    <p className="text-sm text-slate-700 font-semibold">
                      {formatDate(employeeData.dateHired)}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-xl">
                No formal employee profile is linked to this account.
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Security Settings */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                <Lock size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">
                  Security Credentials
                </h3>
                <p className="text-xs text-slate-500">
                  Update your account password securely
                </p>
              </div>
            </div>

            <form onSubmit={handlePasswordChange} className="p-6 md:p-8">
              <div className="space-y-6 max-w-md">
                {/* Username Display Only */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    disabled
                    value={userData?.userName || ""}
                    className="w-full bg-slate-50 border border-slate-200 text-slate-500 px-4 py-3 rounded-xl cursor-not-allowed"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Username cannot be changed directly.
                  </p>
                </div>

                <div className="h-px bg-slate-100 my-6"></div>

                {/* --- TOGGLE LOGIC HERE --- */}
                {!isEditingPassword ? (
                  <div className="pb-4">
                    <button
                      type="button"
                      onClick={() => setIsEditingPassword(true)}
                      className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 bg-white border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
                    >
                      <Key size={18} />
                      Change Password
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6 transition-all duration-300 animate-fade-in">
                    {/* New Password */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter new password"
                          className="w-full border border-slate-200 px-4 py-3 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-12 transition-all outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Confirm New Password */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Retype new password"
                          className="w-full border border-slate-200 px-4 py-3 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-12 transition-all outline-none"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                        >
                          {showConfirmPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-4 flex flex-col sm:flex-row items-center gap-3">
                      <button
                        type="submit"
                        disabled={isSaving || !password || !confirmPassword}
                        className="w-full flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-8 rounded-xl shadow-md shadow-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {isSaving ? (
                          <Loader size={18} color="white" />
                        ) : (
                          <Save size={18} />
                        )}
                        {isSaving ? "Saving..." : "Update Password"}
                      </button>

                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="w-full flex-1 flex items-center justify-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 font-semibold py-3 px-8 rounded-xl transition-all"
                      >
                        <X size={18} />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageAccount;
